import React from 'react';
import { useIntl } from 'react-intl';
import {
  COLORS,
  HEADER_DAY_HEIGHT,
  HEADER_HEIGHT,
  HEADER_MONTH_HEIGHT,
  MIN_PLATE_COL_WIDTH,
  MIN_TIMELINE_COL_WIDTH,
} from '../constants';
import GroupByDropdown from './GroupByDropdown';
import ZoomSwitch from './ZoomSwitch';
import SortArrows from './SortArrows';
import ColumnResizer from './ColumnResizer';
import { GroupBy } from '../utils/grouping';
import { ZoomLevel } from '../utils/timeScale';
import {
  SortDirection,
  SortKey,
  TimelineSort,
  TIMELINE_COLUMN_DEFS,
  TimelineColumnKey,
} from '../utils/timelineColumns';

type Props = {
  groupBy: GroupBy;
  onChangeGroupBy: (next: GroupBy) => void;
  zoom: ZoomLevel;
  onChangeZoom: (next: ZoomLevel) => void;
  leftWidth: number;
  extraColumns: TimelineColumnKey[];
  // Effective width per left column ('plate' + each extra key).
  columnWidths: Record<string, number>;
  onColumnResize: (key: string, width: number) => void;
  sort: TimelineSort;
  onSort: (key: SortKey, direction: SortDirection) => void;
};

const HEADER_LABEL_STYLE: React.CSSProperties = {
  // display:block + width:100% so a long header ellipsises (a global rule makes
  // bare spans display:flex, which disables text-overflow).
  display: 'block',
  width: '100%',
  minWidth: 0,
  fontSize: 11,
  fontWeight: 600,
  color: COLORS.headerSubtleText,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Row wrapping a header label with its up/down sort arrows. minWidth:0 lets the
// label ellipsise while the arrows keep their intrinsic width.
const SortableLabel: React.FC<{
  label: string;
  sortKey: SortKey;
  sort: TimelineSort;
  onSort: (key: SortKey, direction: SortDirection) => void;
}> = ({ label, sortKey, sort, onSort }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
    }}
  >
    <span style={HEADER_LABEL_STYLE}>{label}</span>
    <SortArrows
      active={sort && sort.key === sortKey ? sort.direction : null}
      onSort={(direction) => onSort(sortKey, direction)}
    />
  </div>
);

// A single column header cell in the day-row band (bottom-aligned, like the
// original "Plate" label). Width matches the matching PlateColumn cell; the right
// edge hosts a drag handle to resize the column.
const HeaderCell: React.FC<{
  columnKey: TimelineColumnKey;
  width: number;
  label: string;
  sort: TimelineSort;
  onSort: (key: SortKey, direction: SortDirection) => void;
  onColumnResize: (key: string, width: number) => void;
}> = ({ columnKey, width, label, sort, onSort, onColumnResize }) => (
  <div
    style={{
      position: 'relative',
      width,
      flex: '0 0 auto',
      height: HEADER_DAY_HEIGHT,
      display: 'flex',
      alignItems: 'flex-end',
      padding: '0 12px 10px',
      boxSizing: 'border-box',
    }}
  >
    <SortableLabel
      label={label}
      sortKey={columnKey}
      sort={sort}
      onSort={onSort}
    />
    <ColumnResizer
      columnKey={columnKey}
      width={width}
      minWidth={MIN_TIMELINE_COL_WIDTH}
      onResize={onColumnResize}
    />
  </div>
);

const CornerCell: React.FC<Props> = ({
  groupBy,
  onChangeGroupBy,
  zoom,
  onChangeZoom,
  leftWidth,
  extraColumns,
  columnWidths,
  onColumnResize,
  sort,
  onSort,
}) => {
  const intl = useIntl();
  const plateLabel = intl.formatMessage({
    id: 'fleetadmin.timeline.plate',
    defaultMessage: 'Plate',
  });
  return (
    <div
      style={{
        position: 'sticky',
        left: 0,
        // Above the floating month-label overlay (zIndex:2 in Header) so the corner's
        // opaque bg + shadow cleanly CLIP a month label as it scrolls toward the left
        // edge — otherwise the label slides behind the Group-by dropdown.
        zIndex: 4,
        width: leftWidth,
        flex: '0 0 auto',
        height: HEADER_HEIGHT,
        background: COLORS.headerBg,
        // No right border — matches PlateColumn; shadow alone separates the sticky column.
        boxShadow: COLORS.plateColumnShadow,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Top section (month-row band) — just the zoom switch, left-aligned. */}
      <div
        style={{
          height: HEADER_MONTH_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          background: COLORS.monthRowBg,
          borderBottom: `1px solid ${COLORS.headerBorder}`,
          boxSizing: 'border-box',
        }}
      >
        {/* Width matches the Plate cell content (plate width − horizontal padding)
            so the zoom switch lines up with the Group-by dropdown stacked below it. */}
        <div style={{ width: columnWidths.plate - 24 }}>
          <ZoomSwitch value={zoom} onChange={onChangeZoom} />
        </div>
      </div>

      {/* Bottom section — day-row band: header cell per column (Plate + extras),
          widths matching the PlateColumn body cells. The Plate cell also hosts the
          Group-by dropdown at its top-left (above the "Plate" label). Each cell's
          right edge carries a drag handle to resize the column. */}
      <div style={{ display: 'flex', flex: '1 1 auto' }}>
        <div
          style={{
            position: 'relative',
            width: columnWidths.plate,
            flex: '0 0 auto',
            height: HEADER_DAY_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '8px 12px 10px',
            boxSizing: 'border-box',
          }}
        >
          <GroupByDropdown value={groupBy} onChange={onChangeGroupBy} />
          <SortableLabel
            label={plateLabel}
            sortKey="plate"
            sort={sort}
            onSort={onSort}
          />
          <ColumnResizer
            columnKey="plate"
            width={columnWidths.plate}
            minWidth={MIN_PLATE_COL_WIDTH}
            onResize={onColumnResize}
          />
        </div>
        {extraColumns.map((key) => (
          <HeaderCell
            key={key}
            columnKey={key}
            width={columnWidths[key]}
            label={intl.formatMessage({
              id: TIMELINE_COLUMN_DEFS[key].headerTextId,
              defaultMessage: TIMELINE_COLUMN_DEFS[key].header,
            })}
            sort={sort}
            onSort={onSort}
            onColumnResize={onColumnResize}
          />
        ))}
      </div>
    </div>
  );
};

export default CornerCell;
